import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StepProgress } from "../components/StepProgress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { ArrowRight, ArrowLeft, Cpu, Info, ChevronDown, ChevronUp, Check, Brain, Sparkles } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { updateAssessment, getAssessment, getAssessmentById, submitAssessment, trackEvent, getQuestionsGrouped, questionAppliesToScenario } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { PRODUCTION_INDUSTRIES, PRODUCTION_WH_TYPES } from "../data/module3Questions";
import { HelpAssistantPanel } from "../components/HelpAssistantPanel";
import DemoProfileBanner from "../components/DemoProfileBanner";
import { ContributorsSection } from "../components/ContributorsSection";

const NUMERIC_TYPES = new Set(["number", "optional_number"]);
const TEXT_TYPES = new Set(["text", "optional_text"]);
const LONGTEXT_TYPES = new Set(["long_text", "textarea"]);
const REQUIRED_BY_TYPE = (q) => {
  // Legacy optional_* types are always non-required
  if (q.answer_type === "optional_number" || q.answer_type === "optional_text") return false;
  // Unified types: honour the is_required flag (default true)
  return q.is_required !== false;
};

// Shared answered-check used by progress counters AND required-question
// validation so all scenarios (Greenfield / Brownfield / Cloud Migration)
// behave identically. Supports number, short/long text, single & multi select.
const isAnsweredValue = (q, v) => {
  if (NUMERIC_TYPES.has(q.answer_type)) return v !== undefined && v !== null && v !== "";
  if (TEXT_TYPES.has(q.answer_type) || LONGTEXT_TYPES.has(q.answer_type)) return typeof v === "string" && v.trim().length > 0;
  if (q.answer_type === "multi_select") return Array.isArray(v) && v.length > 0;
  return v !== undefined && v !== null && v !== "";
};

// ---------------------------------------------------------------------------
// SmartEvaluate Insights — section-level, signal-based, non-intrusive.
//
// Design intent:
//   • ONE insight per section of the active tab.
//   • Insights derive from high-level SIGNALS inferred from the option values
//     the user selected — never from specific field_keys. This keeps the
//     logic future-proof: a new question whose answer value is "high" simply
//     contributes to the same complexity signal as any existing question.
//   • Content is deliberately indicative ("suggests", "points to") — it
//     never proposes SAP solutions, Basic/Advanced labels, or recommendations.
// ---------------------------------------------------------------------------

const INSIGHT_MIN_ANSWERS = 2; // below this, show placeholder

// Classify an answer value into a complexity intensity 0 / 1 / 2.
// Works on both dropdown/single-select values (strings) and multi-select
// arrays. Numeric values fall back to "moderate" (1).
function intensityOf(v) {
  if (v === undefined || v === null || v === "") return null;
  if (Array.isArray(v)) {
    if (v.length === 0) return null;
    // Multi-select: the single highest intensity wins — e.g. picking one
    // "advanced" option should count as a high-complexity signal.
    let best = null;
    for (const item of v) {
      const i = intensityOf(item);
      if (i !== null && (best === null || i > best)) best = i;
    }
    return best;
  }
  const s = String(v).toLowerCase();
  if (!s) return null;
  if (/(very_high|very-high|very_large|multi_site|multi-site|extensive|advanced|large|complex|high|heavy|robotic|predictive|real.?time|urgent|transformational|strong|frequent|serial|rfid)/.test(s)) return 2;
  if (/(medium|moderate|some|partial|growing|evolving|mid|structured|batch|hu_|wave|scheduled)/.test(s)) return 1;
  if (/(very_low|very-low|none|no_|low|basic|simple|single|minimal|small|manual|stable|few|limited|rare)/.test(s)) return 0;
  // Pure numbers → neutral
  if (/^[-+]?\d+(\.\d+)?$/.test(s)) return 1;
  return 1; // unknown dropdown labels default neutral, never null
}

// Pick a template index (0–4) from the intensity mix in a section.
function selectTemplateIndex(sectionAnswers) {
  const intensities = Object.values(sectionAnswers || {})
    .map(intensityOf)
    .filter((x) => x !== null);
  if (intensities.length < INSIGHT_MIN_ANSWERS) return -1; // placeholder
  const avg = intensities.reduce((a, b) => a + b, 0) / intensities.length;
  // 5 buckets mapped onto the avg scale [0..2].
  // Chosen so the extremes require stronger evidence.
  if (avg < 0.45) return 0; // Template 1 — simple / basic / low
  if (avg < 0.95) return 1; // Template 2 — structured / moderate / early
  if (avg < 1.35) return 2; // Template 3 — distributed / HU / batch / RF
  if (avg < 1.7) return 3;  // Template 4 — variable / growth / high
  return 4;                 // Template 5 — complex / advanced / transformational
}

// Section copy banks — 5 templates each, ordered low → high complexity.
// Tone: senior SAP consultant. Each sentence starts with an observation
// ("Your setup…" / "Your current setup…" / "Based on your inputs…"), ends
// with a light implication, and stays within ~20–25 words. No recommendations,
// no product names, no "Basic vs Advanced" framing.
const SECTION_INSIGHTS = {
  org: [
    "Your setup reflects a lean warehouse footprint with limited operational complexity, typically seen in early-stage or small-scale distribution environments.",
    "Your current setup suggests a structured, moderately sized warehouse with defined processes and a predictable operating rhythm.",
    "Based on your inputs, your operation aligns with a distributed warehouse network requiring coordination across multiple locations.",
    "Your setup points toward a high-volume operation where execution demands are steadily intensifying across the network.",
    "Your current profile reflects a large-scale, high-complexity warehouse operation often associated with significant process, resource, and governance depth.",
  ],
  inbound: [
    "Your setup reflects relatively straightforward inbound flows with limited handling or inspection complexity.",
    "Your current setup suggests moderate inbound requirements with emerging structure around receiving and staging.",
    "Based on your inputs, your inbound flows align with handling-unit-driven operations typically seen in controlled supplier environments.",
    "Your setup points toward variable inbound volumes that often require tighter coordination across receiving, inspection, and staging.",
    "Your current inbound profile reflects multi-layered handling and inspection activity often associated with complex supplier ecosystems.",
  ],
  outbound: [
    "Your setup reflects straightforward outbound flows with limited picking and packing complexity.",
    "Your current setup suggests structured picking with moderate fulfilment demands across a steady order profile.",
    "Based on your inputs, your outbound operations align with batch- or wave-based execution patterns often seen in growing distribution networks.",
    "Your setup points toward increasing picking and packing complexity driven by more varied and time-sensitive order flows.",
    "Your current outbound profile reflects a high-volume, multi-step fulfilment model typically seen in mature distribution environments.",
  ],
  inventory: [
    "Your setup reflects simple inventory practices with limited tracking or control demands.",
    "Your current setup suggests a need for more structured tracking and day-to-day inventory visibility.",
    "Based on your inputs, your inventory model aligns with batch-level control often associated with regulated or perishable goods handling.",
    "Your setup points toward higher expectations for accuracy and near real-time inventory visibility across the operation.",
    "Your current inventory profile reflects complex tracking requirements spanning multiple dimensions typically seen in advanced distribution environments.",
  ],
  automation: [
    "Your current setup reflects a largely manual operation with limited automation in day-to-day execution.",
    "Your setup suggests early steps toward automation, with selective technology adoption in core activities.",
    "Based on your inputs, your operation aligns with RF- or mobile-led execution typically associated with structured warehouse control.",
    "Your setup points toward a connected environment where systems and processes are increasingly integrated across operations.",
    "Your current profile reflects strong potential for automation and system-led execution often seen in modern, technology-forward warehouses.",
  ],
  integration: [
    "Your setup reflects limited system integration with relatively simple data exchange across the landscape.",
    "Your current setup suggests moderate integration across core systems and operational flows.",
    "Based on your inputs, your landscape aligns with coordination across multiple systems and interdependent business processes.",
    "Your setup points toward a need for timely, synchronised data exchange across connected systems.",
    "Your current landscape reflects a dense integration environment with multiple dependencies typically seen in mature enterprise operations.",
  ],
  reporting: [
    "Your setup reflects reporting focused on basic operational visibility and day-to-day oversight.",
    "Your current setup suggests structured reporting with regular KPI tracking and performance reviews.",
    "Based on your inputs, your reporting rhythm aligns with continuous operational monitoring typically seen in performance-driven teams.",
    "Your setup points toward demand for deeper analytics and richer decision-support across warehouse operations.",
    "Your current profile reflects a need for predictive insights and data-driven decision support often associated with advanced operations.",
  ],
  pain_points: [
    "Your current setup reflects stable operations with limited immediate transformation pressure.",
    "Your setup suggests some operational friction pointing toward targeted, structured improvements in the near term.",
    "Based on your inputs, your trajectory aligns with growth that will steadily raise expectations on warehouse capability.",
    "Your setup points toward time-sensitive improvement needs typically associated with capacity, accuracy, or customer-service pressure.",
    "Your current profile reflects a strong transformation agenda often seen in organisations preparing for the next phase of scale.",
  ],
  // Legacy / optional sections — same tone as the others.
  production: [
    "Your setup reflects light production linkage with limited interaction with warehouse flows.",
    "Your current setup suggests moderate production-warehouse coordination around staging and component availability.",
    "Based on your inputs, your operation aligns with structured staging and movement patterns typically seen in scheduled production models.",
    "Your setup points toward tight production-to-warehouse coupling that adds coordination complexity across materials and timing.",
    "Your current profile reflects a deeply integrated production and warehouse environment often associated with make-to-order or high-mix manufacturing.",
  ],
  migration_readiness: [
    "Your setup reflects limited migration pressure with few system-level constraints on the current landscape.",
    "Your current setup suggests moderate readiness for a structured system transition.",
    "Based on your inputs, your landscape aligns with a phased migration path typically seen in stable enterprise environments.",
    "Your setup points toward legacy dependencies that often require careful sequencing during migration planning.",
    "Your current profile reflects significant transformation and migration complexity often associated with long-running, multi-system estates.",
  ],
};

function InsightsPanel({ activeTab, tabLabel, answers, visibleSections, activeSectionId }) {
  // tabLabel is resolved by the parent (group name) and passed in — this
  // component is defined outside Module3Page so it has no access to the
  // parent's tabLabel callback.
  const siteLabel = typeof tabLabel === "function" ? tabLabel(activeTab) : (tabLabel || activeTab);

  // Pick the section that is currently in view — fall back to the first
  // section with enough signal, then the first visible section.
  const activeSection = useMemo(() => {
    const byId = (id) => visibleSections.find((s) => s.id === id);
    return (
      byId(activeSectionId) ||
      visibleSections.find((s) => {
        const subset = {};
        for (const q of s.questions || []) {
          const k = q.field_key || q.id;
          if (k in (answers || {})) subset[k] = answers[k];
        }
        return selectTemplateIndex(subset) !== -1;
      }) ||
      visibleSections[0] ||
      null
    );
  }, [activeSectionId, visibleSections, answers]);

  // Compute the single insight for the active section.
  const insight = useMemo(() => {
    if (!activeSection) return null;
    const bank = SECTION_INSIGHTS[activeSection.id] || SECTION_INSIGHTS.org;
    const subset = {};
    for (const q of activeSection.questions || []) {
      const k = q.field_key || q.id;
      if (k in (answers || {})) subset[k] = answers[k];
    }
    const idx = selectTemplateIndex(subset);
    return {
      sectionTitle: activeSection.title,
      sectionId: activeSection.id,
      text: idx === -1 ? null : bank[idx],
      templateIndex: idx,
    };
  }, [activeSection, answers]);

  return (
    <aside
      data-testid="insights-panel"
      data-active-section-id={activeSection?.id || ""}
      data-template-index={insight?.templateIndex ?? ""}
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E5E7EB] bg-[#F8FAFC]">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-sm bg-[#6E5BCE]/10 flex items-center justify-center">
            <Brain className="w-3.5 h-3.5 text-[#6E5BCE]" strokeWidth={1.75} />
          </div>
          <div>
            <p
              className="text-[11px] font-semibold text-[#111827] tracking-wide"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              SmartEvaluate Insight
            </p>
            <p className="text-[10px] text-[#6B7280]">
              Live insight based on your inputs · {siteLabel}
            </p>
          </div>
          <span className="ml-auto text-[9px] font-semibold tracking-wide text-[#6E5BCE]">
            Powered by PRIVA
          </span>
        </div>
      </div>

      {/* Single insight body */}
      <div className="px-4 py-4" data-testid="insights-body">
        {insight && (
          <p
            data-testid="insights-section-label"
            className="text-[10px] font-semibold text-[#025979] uppercase tracking-wider mb-2"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {insight.sectionTitle}
          </p>
        )}
        {insight?.text ? (
          <p
            key={`${insight.sectionId}-${insight.templateIndex}`}
            data-testid="insights-copy"
            className="text-[13px] text-[#1F2937] leading-relaxed transition-opacity duration-200"
          >
            {insight.text}
          </p>
        ) : (
          <p
            data-testid="insights-empty-copy"
            className="text-[13px] text-[#4B5563] leading-relaxed"
          >
            Insights will refine as you continue your evaluation.
          </p>
        )}
      </div>

      {/* Disclaimer — directly below the insight, subtle */}
      <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#F8FAFC]">
        <p
          data-testid="insights-disclaimer"
          className="text-[10px] text-[#9CA3AF] leading-relaxed"
        >
          Preliminary observation based on your inputs. Detailed recommendations will be included in the final report.
        </p>
      </div>
    </aside>
  );
}

function QuestionCard({ q, value, onChange, tabId, error }) {
  const qKey = q.field_key || q.id;
  const type = q.answer_type || "single_select";

  // Numeric input
  if (NUMERIC_TYPES.has(type)) {
    const required = REQUIRED_BY_TYPE(q);
    return (
      <div className="mb-4 last:mb-0">
        <p className="text-sm text-[#111827] font-medium mb-1">
          {q.question_text || q.label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {!required && <span className="text-[10px] text-[#9CA3AF] ml-2 font-normal">(optional)</span>}
        </p>
        {q.help_text && <p className="text-[11px] text-[#6B7280] mb-2">{q.help_text}</p>}
        <div className="flex items-center gap-2 max-w-xs">
          <Input
            data-testid={`q-${tabId}-${qKey}`}
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(qKey, e.target.value)}
            placeholder={q.placeholder || ""}
            min={q.min_value ?? undefined}
            max={q.max_value ?? undefined}
            className={`rounded-sm text-sm ${error ? "border-red-400" : ""}`}
          />
          {q.unit && <span className="text-sm text-[#6B7280] shrink-0">{q.unit}</span>}
        </div>
        {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  // Text input
  if (TEXT_TYPES.has(type)) {
    const required = REQUIRED_BY_TYPE(q);
    return (
      <div className="mb-4 last:mb-0">
        <p className="text-sm text-[#111827] font-medium mb-1">
          {q.question_text || q.label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {!required && <span className="text-[10px] text-[#9CA3AF] ml-2 font-normal">(optional)</span>}
        </p>
        {q.help_text && <p className="text-[11px] text-[#6B7280] mb-2">{q.help_text}</p>}
        <Input
          data-testid={`q-${tabId}-${qKey}`}
          value={value ?? ""}
          onChange={(e) => onChange(qKey, e.target.value)}
          placeholder={q.placeholder || ""}
          className={`rounded-sm text-sm ${error ? "border-red-400" : ""}`}
        />
        {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  // Long text
  if (LONGTEXT_TYPES.has(type)) {
    const required = REQUIRED_BY_TYPE(q);
    return (
      <div className="mb-4 last:mb-0">
        <p className="text-sm text-[#111827] font-medium mb-1">
          {q.question_text || q.label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {!required && <span className="text-[10px] text-[#9CA3AF] ml-2 font-normal">(optional)</span>}
        </p>
        {q.help_text && <p className="text-[11px] text-[#6B7280] mb-2">{q.help_text}</p>}
        <Textarea
          data-testid={`q-${tabId}-${qKey}`}
          value={value ?? ""}
          onChange={(e) => onChange(qKey, e.target.value)}
          placeholder={q.placeholder || ""}
          rows={4}
          className={`rounded-sm text-sm ${error ? "border-red-400" : ""}`}
        />
        {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  // Dropdown
  if (type === "dropdown") {
    const required = REQUIRED_BY_TYPE(q);
    return (
      <div className="mb-4 last:mb-0">
        <p className="text-sm text-[#111827] font-medium mb-2">
          {q.question_text || q.label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </p>
        {q.help_text && <p className="text-[11px] text-[#6B7280] -mt-1 mb-2">{q.help_text}</p>}
        <select
          data-testid={`q-${tabId}-${qKey}`}
          value={value || ""}
          onChange={(e) => onChange(qKey, e.target.value)}
          className={`w-full max-w-md rounded-sm border text-sm px-3 py-2 bg-white ${error ? "border-red-400" : "border-[#E5E7EB]"}`}
        >
          <option value="">{q.placeholder || "Select..."}</option>
          {(q.answer_options || q.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  // Multi-select
  if (type === "multi_select") {
    const required = REQUIRED_BY_TYPE(q);
    const selected = Array.isArray(value) ? value : (value ? [value] : []);
    const toggle = (v) => {
      const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
      onChange(qKey, next);
    };
    return (
      <div className="mb-4 last:mb-0">
        <p className="text-sm text-[#111827] font-medium mb-2">
          {q.question_text || q.label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </p>
        {q.help_text && <p className="text-[11px] text-[#6B7280] -mt-1 mb-2">{q.help_text}</p>}
        <div className="grid grid-cols-2 gap-2">
          {(q.answer_options || q.options || []).map((o) => (
            <label
              key={o.value}
              data-testid={`q-${tabId}-${qKey}-${o.value}`}
              className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer text-xs ${selected.includes(o.value) ? "border-[#025979] bg-[#E0F2FE]/50 text-[#025979]" : "border-[#E5E7EB] hover:border-[#D1D5DB] text-[#374151]"}`}
            >
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
        {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  // Default — single select (scoring)
  const required = REQUIRED_BY_TYPE(q);
  return (
    <div className="mb-4 last:mb-0">
      <p className="text-sm text-[#111827] font-medium mb-2">
        {q.question_text || q.label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </p>
      {q.help_text && <p className="text-[11px] text-[#6B7280] -mt-1 mb-2">{q.help_text}</p>}
      <div className="grid grid-cols-3 gap-2">
        {(q.answer_options || q.options || []).map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`q-${tabId}-${qKey}-${o.value}`}
            onClick={() => onChange(qKey, o.value)}
            className={`text-left p-2.5 rounded-sm border cursor-pointer transition-colors text-xs ${
              value === o.value
                ? "border-[#025979] bg-[#E0F2FE]/50 font-medium text-[#025979]"
                : "border-[#E5E7EB] hover:border-[#D1D5DB] hover:bg-gray-50 text-[#374151]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function CollapsibleSection({ section, answers, errors, onChange, tabId, defaultOpen, forceOpen, highlight, assigned }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  const isAnswered = (q) => isAnsweredValue(q, answers[q.field_key || q.id]);
  const requiredQuestions = section.questions.filter(REQUIRED_BY_TYPE);
  const answeredCount = requiredQuestions.filter(isAnswered).length;
  const total = requiredQuestions.length;
  const complete = total > 0 && answeredCount === total;

  // Group questions by subsection_heading so we can render "Operational Scale" sub-heading inline
  const groups = [];
  const seen = new Map();
  for (const q of section.questions) {
    const key = q.subsection_heading || "";
    if (!seen.has(key)) {
      const entry = { heading: key, questions: [] };
      seen.set(key, entry);
      groups.push(entry);
    }
    seen.get(key).questions.push(q);
  }
  const hasOperationalScale = groups.some((g) => g.heading === "Operational Scale");

  return (
    <div className={`border rounded-sm overflow-hidden ${highlight ? "border-red-300 ring-1 ring-red-200" : "border-[#E5E7EB]"}`} data-m3-section={section.id} data-testid={`section-${tabId}-${section.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {complete ? (
            <div className="w-5 h-5 rounded-full bg-[#025979] flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-[#D1D5DB] flex items-center justify-center">
              <span className="text-[9px] font-bold text-[#6B7280]">{answeredCount}</span>
            </div>
          )}
          <span className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>{section.title}</span>
          {assigned && (
            <span data-testid={`assigned-badge-${tabId}-${section.id}`} className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-[#E0F2FE] text-[#025979] border border-[#025979]/20">
              Assigned to you
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#9CA3AF]">{answeredCount}/{total}</span>
          {open ? <ChevronUp className="w-4 h-4 text-[#6B7280]" /> : <ChevronDown className="w-4 h-4 text-[#6B7280]" />}
        </div>
      </button>
      {open && (
        <div className="px-5 py-4 border-t border-[#E5E7EB] bg-[#FAFBFC]">
          {section.id === "org" && hasOperationalScale && (
            <div className="mb-4 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm" data-testid={`guidance-${tabId}-${section.id}`}>
              <p className="text-[11px] text-[#025979] leading-relaxed">
                Please provide your best estimates. Exact numbers are not required and can be refined later during detailed evaluation or implementation planning.
              </p>
            </div>
          )}
          {groups.map((grp, gi) => (
            <div key={gi}>
              {grp.heading && (
                <div className="mt-2 mb-3 pb-1 border-b border-[#E5E7EB]" data-testid={`subheading-${tabId}-${section.id}-${grp.heading.replace(/\s+/g, '_').toLowerCase()}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#025979]">{grp.heading}</p>
                </div>
              )}
              {grp.questions.map((q) => (
                <div key={q.field_key || q.id} data-field-key={q.field_key || q.id}>
                  <QuestionCard
                    q={q}
                    value={answers[q.field_key || q.id]}
                    onChange={onChange}
                    tabId={tabId}
                    error={errors?.[q.field_key || q.id]}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Module3Page() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [repSites, setRepSites] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [tabAnswers, setTabAnswers] = useState({});
  const [industry, setIndustry] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [rawSections, setRawSections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [visibleSectionId, setVisibleSectionId] = useState("");
  const [activeQuestionId, setActiveQuestionId] = useState("");
  const [scenarioType, setScenarioType] = useState("");
  const [focusSectionId, setFocusSectionId] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [triedSubmit, setTriedSubmit] = useState(false);
  const prevTabRef = useRef("");

  // Phase 4 — collaboration: field-level autosave, roles, save/submit.
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const [ownerEmail, setOwnerEmail] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [myAssignedSections, setMyAssignedSections] = useState([]);
  const [myAssignedGroups, setMyAssignedGroups] = useState([]);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [savingEval, setSavingEval] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const saveTimersRef = useRef({});      // "site::qId" -> debounce timeout id
  const pendingValsRef = useRef({});     // "site::qId" -> latest value awaiting save
  const inflightRef = useRef(new Set()); // in-flight save promises
  const activeTabRef = useRef("");
  const activeQuestionRef = useRef("");

  // ---------------------------------------------------------------------------
  // Grouped vs legacy Step 3 (Phase 2).
  //   • GROUPED (assessment.warehouse_groups present): the top-level assessment
  //     unit is the Warehouse Group. Tabs are keyed by the stable group UUID and
  //     answers live under module_data.module3_groups[groupId][qId] — an
  //     independent answer set per group. The active group's assessment_type
  //     drives the existing scenario question-filtering.
  //   • LEGACY (no groups): unchanged — warehouse-type tabs, answers under
  //     module_data.module3[site][qId].
  // ---------------------------------------------------------------------------
  const groupById = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  // Warehouse Groups are the universal Step 3 unit. Answers are always keyed by
  // the stable group UUID under module_data.module3_groups[groupId][field_key].
  const answerRoot = "module3_groups";
  const scenarioForTab = useCallback(
    (tab) => (groupById[tab]?.assessment_type || ""),
    [groupById]
  );
  const tabLabel = useCallback(
    (tab) => (groupById[tab]?.name || tab),
    [groupById]
  );
  // Derive the visible sections for a given scenario from the raw (unfiltered)
  // section list. migration_readiness is brownfield-only; empty sections drop.
  const buildSections = useCallback((scenario) => {
    return rawSections
      .filter((s) => s.id !== "migration_readiness" || scenario === "brownfield")
      .map((s) => ({ ...s, questions: s.questions.filter((q) => questionAppliesToScenario(q, scenario)) }))
      .filter((s) => s.questions.length > 0);
  }, [rawSections]);
  const activeScenario = scenarioForTab(activeTab);
  const apiSections = useMemo(() => buildSections(activeScenario), [buildSections, activeScenario]);

  // Track which section is currently "in view" so the sticky Insights panel
  // can show a single context-aware insight. The chosen section = the last
  // `[data-m3-section]` whose top has scrolled above the anchor (below the
  // sticky tab bar). This stays rock-solid during accordion open/close and
  // works for both collapsed and expanded sections.
  useEffect(() => {
    if (!loaded) return;
    const ANCHOR_Y = 300; // just below the sticky tab bar
    let raf = 0;
    const update = () => {
      const els = Array.from(document.querySelectorAll("[data-m3-section]"));
      if (els.length === 0) return;
      let best = els[0].getAttribute("data-m3-section");
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.top <= ANCHOR_Y) best = el.getAttribute("data-m3-section");
        else break;
      }
      if (best) setVisibleSectionId(best);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; update(); });
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [loaded, activeTab, apiSections]);

  const SECTION_TITLES = {
    system_infrastructure: "System & Infrastructure",
    data_volume: "Data Volume",
    integration_landscape: "Integration Landscape",
    custom_development: "Custom Development",
    operations_cutover: "Operations & Cutover",
    migration_objectives: "Migration Objectives",
    current_parallel_projects: "Current & Parallel Projects",
    org: "Warehouse Profile & Volumes",
    inbound: "Inbound Processes",
    outbound: "Outbound Processes",
    inventory: "Internal Movements & Inventory",
    production: "Production & Quality",
    automation: "Smart Warehousing",
    integration: "Integrations & Interfaces",
    reporting: "Reporting & KPIs",
    pain_points: "Business Priorities & Future Strategy",
    migration_readiness: "Current System & Migration Readiness",
  };

  // Contributor invite deep-link: /module-3?invite=1&aid=<uuid>&cemail=<email>
  // points this browser at the SHARED assessment and the contributor's OWN
  // email, then cleans the URL. Runs before the loader below (declaration
  // order) so localStorage is set when it reads. Owner flow (no invite params)
  // is completely untouched.
  const [inviteParams, setInviteParams] = useSearchParams();
  useEffect(() => {
    if (inviteParams.get("invite") === "1") {
      const aid = inviteParams.get("aid");
      const cemail = inviteParams.get("cemail");
      if (aid) localStorage.setItem("warehousex_assessment_id", aid);
      if (cemail) localStorage.setItem("warehousex_email", cemail.trim().toLowerCase());
      const next = new URLSearchParams(inviteParams);
      next.delete("invite"); next.delete("aid"); next.delete("cemail");
      setInviteParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load questions from API + assessment data
  useEffect(() => {
    const load = async () => {
      const email = localStorage.getItem("warehousex_email");
      const draftId = localStorage.getItem("warehousex_assessment_id");
      setCurrentEmail((email || "").trim().toLowerCase());
      try {
        // Need to know scenario BEFORE filtering sections, so fetch assessment
        // first when available. BUT only if both draft keys are present —
        // a stale email without an assessment_id means the previous evaluation
        // was submitted and we must NOT rehydrate.
        let scenario = "";
        let assessment = null;
        if (email && draftId) {
          try {
            assessment = await getAssessment(email);
            if (assessment?.status === "submitted" || assessment?.status === "completed") {
              // Submitted evaluations are read-only — don't leak them into the
              // editable flow.
              assessment = null;
            }
          } catch (_) {
            /* ignore — fall back to empty scenario */
          }
          // Contributor invite: the owner-scoped email lookup won't match a
          // contributor's email, so load the SAME assessment by its id to show
          // the shared Step 3 answers. Owner flow already resolved above, so
          // this fallback never runs for owners.
          if (!assessment) {
            try {
              const byId = await getAssessmentById(draftId);
              if (byId && byId.status !== "submitted" && byId.status !== "completed") {
                assessment = byId;
              }
            } catch (_) {
              /* ignore */
            }
          }
          scenario = assessment?.scenario_type || "";
        }
        // Cloud Migration is being built as its own Module 3 flow: it shows
        // ONLY sections whose questions are tagged for cloud_migration (via the
        // true scenario below). Greenfield/Brownfield are untouched.

        // Load questions from API. Render EVERY active Process Assessment
        // section the Question Bank returns — the canonical list only fixes the
        // display order for known sections; any other active section is appended
        // so new admin sections appear automatically (no hardcoded allow-list).
        setScenarioType(scenario);
        const qData = await getQuestionsGrouped("module3");
        const CANONICAL_ORDER = ["system_infrastructure", "data_volume", "integration_landscape", "custom_development", "operations_cutover", "migration_objectives", "current_parallel_projects", "org", "inbound", "outbound", "inventory", "production", "automation", "integration", "reporting", "pain_points", "migration_readiness"];
        const apiSecKeys = Object.keys(qData.sections || {}).filter((s) => qData.sections[s]?.length > 0);
        const orderedKeys = [
          ...CANONICAL_ORDER.filter((s) => apiSecKeys.includes(s)),
          ...apiSecKeys.filter((s) => !CANONICAL_ORDER.includes(s)),
        ];
        // Build the RAW section list (all questions, no scenario filter). The
        // visible per-tab sections are derived later via buildSections() using
        // the active tab's scenario — the page scenario for legacy evaluations
        // or the active group's assessment_type for grouped evaluations.
        const rawSecs = orderedKeys.map((s) => ({
          id: s,
          title: SECTION_TITLES[s] || s,
          // Production & Quality is shown only when a production industry or a
          // Manufacturing Plant Warehouse type is in scope for the tab.
          conditional: s === "production",
          questions: qData.sections[s].map((q) => ({
            ...q,
            id: q.field_key || q.id,
            label: q.question_text,
            options: q.answer_options,
          })),
        }));
        setRawSections(rawSecs);

        // Load assessment data
        if (assessment) {
          const a = assessment;
          setOwnerEmail(a.email || "");
          // Section-assignment (responsibility) for the current contributor.
          const meLc = (email || "").trim().toLowerCase();
          const mine = (a.collaborators || []).find(
            (c) => (c.email || "").trim().toLowerCase() === meLc && (c.status || "active") === "active"
          );
          setMyAssignedSections(mine?.assigned_sections || []);
          setMyAssignedGroups(mine?.assigned_groups || []);
          const m2 = a.module_data?.module2 || {};
          setIndustry(m2.industry || a.scenario_type || "");
          const wgs = Array.isArray(a.warehouse_groups) ? a.warehouse_groups : [];
          setGroups(wgs);

          // Warehouse Groups are the universal Step 3 unit — tabs keyed by the
          // stable group UUID; answers under module_data.module3_groups[groupId].
          // (No legacy warehouse-type / representative-site fallback.)
          const tabs = wgs.map((g) => g.id);
          setRepSites(tabs);
          if (tabs.length > 0) setActiveTab(tabs[0]);
          // Seed question-bank default_value per group tab where no saved
          // value exists (e.g. Operating Days/Week=5, Weeks/Year=50). Mirrors
          // Module1/Module2 default handling.
          const defaults = {};
          rawSecs.forEach((s) => (s.questions || []).forEach((q) => {
            const dv = q.default_value;
            if (dv !== null && dv !== undefined && String(dv) !== "") defaults[q.id] = dv;
          }));
          const applyDefaults = (ans, group) => {
            const src = ans || {};
            const out = { ...src };
            Object.entries(defaults).forEach(([k, dv]) => {
              if (out[k] === null || out[k] === undefined || out[k] === "") out[k] = dv;
            });
            // "Number of warehouses of this type" defaults to the count entered
            // at group creation (wins over the generic bank default of "1"),
            // but stays fully editable by the client.
            const wc = Number(group?.warehouse_count);
            const origEmpty = src.op_warehouse_count === null || src.op_warehouse_count === undefined || src.op_warehouse_count === "";
            if (origEmpty && Number.isFinite(wc) && wc > 0) {
              out.op_warehouse_count = wc;
            }
            return out;
          };
          const stored = a.module_data?.module3_groups;
          const wgById = {};
          wgs.forEach((g) => { if (g && g.id) wgById[g.id] = g; });
          const init = {};
          tabs.forEach((t) => { init[t] = applyDefaults(stored && typeof stored === "object" ? stored[t] : {}, wgById[t]); });
          setTabAnswers(init);

          // Persist Admin-configured defaults that filled previously-empty
          // fields, so server-side submit validation and downstream scoring see
          // them. Defaults applied only to local state (above) never otherwise
          // reach the backend, which caused "N required questions still need an
          // answer" on submit despite the UI showing 100%. Only the owner (who
          // can edit every group and is the one who submits) persists them.
          try {
            const meLc2 = (email || "").trim().toLowerCase();
            const ownerLc = (a.email || "").trim().toLowerCase();
            const amOwner = !!(meLc2 && ownerLc && meLc2 === ownerLc);
            const aid = localStorage.getItem("warehousex_assessment_id") || a.id;
            if (amOwner && aid) {
              tabs.forEach((t) => {
                const storedTab = (stored && typeof stored === "object" ? stored[t] : {}) || {};
                const add = {};
                Object.entries(defaults).forEach(([k, dv]) => {
                  if (storedTab[k] === null || storedTab[k] === undefined || storedTab[k] === "") add[k] = dv;
                });
                // Persist the group-creation warehouse count as the default
                // so submit validation / scoring / aggregation see it.
                const wc = Number(wgById[t]?.warehouse_count);
                if (
                  (storedTab.op_warehouse_count === null || storedTab.op_warehouse_count === undefined || storedTab.op_warehouse_count === "") &&
                  Number.isFinite(wc) && wc > 0
                ) {
                  add.op_warehouse_count = wc;
                }
                if (Object.keys(add).length) {
                  updateAssessment(aid, { module_data: { [answerRoot]: { [t]: add } } }).catch(() => { /* non-fatal */ });
                }
              });
            }
          } catch { /* non-fatal: defaults still applied in local state */ }
        }
      } catch (e) {
        console.error("Failed to load:", e);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  const showProduction = useCallback((tab) => {
    if (PRODUCTION_INDUSTRIES.includes(industry)) return true;
    return (groupById[tab]?.warehouse_types || []).some((t) => PRODUCTION_WH_TYPES.includes(t));
  }, [industry, groupById]);

  // Keep refs in sync so async server-merges never clobber the field the user
  // is actively editing.
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { activeQuestionRef.current = activeQuestionId; }, [activeQuestionId]);

  // Merge the latest SERVER Step 3 state into local answers WITHOUT clobbering
  // any field this user is actively editing or has a pending unsaved save for.
  const mergeServerAnswers = useCallback((server) => {
    setTabAnswers((prev) => {
      const out = { ...prev };
      Object.keys(server || {}).forEach((site) => {
        const merged = { ...(out[site] || {}) };
        Object.entries(server[site] || {}).forEach(([qId, val]) => {
          const key = `${site}::${qId}`;
          const isPending = key in pendingValsRef.current || key in saveTimersRef.current;
          const isActive = site === activeTabRef.current && qId === activeQuestionRef.current;
          if (!isPending && !isActive) merged[qId] = val;
        });
        out[site] = merged;
      });
      return out;
    });
  }, []);

  const refreshSharedAnswers = useCallback(async () => {
    const id = localStorage.getItem("warehousex_assessment_id");
    if (!id) return;
    try {
      const fresh = await getAssessmentById(id);
      if (fresh?.module_data?.[answerRoot]) mergeServerAnswers(fresh.module_data[answerRoot]);
    } catch { /* ignore transient refresh errors */ }
  }, [mergeServerAnswers, answerRoot]);

  // Persist ONLY the single changed field so a stale full-object write can
  // never overwrite answers another contributor saved after this browser
  // loaded. This is the core concurrency protection.
  const flushKey = useCallback((site, qId) => {
    const key = `${site}::${qId}`;
    if (!(key in pendingValsRef.current)) return undefined;
    const val = pendingValsRef.current[key];
    delete pendingValsRef.current[key];
    const id = localStorage.getItem("warehousex_assessment_id");
    if (!id) return undefined;
    setSaveStatus("saving");
    const p = updateAssessment(id, { module_data: { [answerRoot]: { [site]: { [qId]: val } } } })
      .catch((e) => { console.error("Field save failed:", e); })
      .finally(() => {
        inflightRef.current.delete(p);
        if (inflightRef.current.size === 0 && Object.keys(pendingValsRef.current).length === 0) {
          setSaveStatus("saved");
        }
      });
    inflightRef.current.add(p);
    return p;
  }, [answerRoot]);

  const scheduleFieldSave = useCallback((site, qId, val) => {
    const key = `${site}::${qId}`;
    pendingValsRef.current[key] = val;
    setSaveStatus("saving");
    if (saveTimersRef.current[key]) clearTimeout(saveTimersRef.current[key]);
    saveTimersRef.current[key] = setTimeout(() => {
      delete saveTimersRef.current[key];
      flushKey(site, qId);
    }, 600);
  }, [flushKey]);

  // Fire all debounced saves immediately and await every in-flight save.
  const flushPendingSaves = useCallback(async () => {
    Object.keys(saveTimersRef.current).forEach((key) => {
      clearTimeout(saveTimersRef.current[key]);
      delete saveTimersRef.current[key];
      const idx = key.indexOf("::");
      flushKey(key.slice(0, idx), key.slice(idx + 2));
    });
    await Promise.all(Array.from(inflightRef.current));
    if (inflightRef.current.size > 0) await Promise.all(Array.from(inflightRef.current));
  }, [flushKey]);

  const updateAnswer = useCallback((qId, val) => {
    setSubmitError("");
    setSaveMsg("");
    setTabAnswers((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [qId]: val },
    }));
    scheduleFieldSave(activeTab, qId, val);
  }, [activeTab, scheduleFieldSave]);

  // Legacy tab-switch autosave now flushes pending FIELD saves (never a full
  // object write), keeping concurrent contributors safe.
  const saveCurrentTab = useCallback(async () => {
    await flushPendingSaves();
  }, [flushPendingSaves]);

  const switchTab = async (tab) => {
    if (tab === activeTab) return;
    await flushPendingSaves();
    prevTabRef.current = activeTab;
    setActiveTab(tab);
    setActiveQuestionId("");
    // Pull in answers other contributors may have saved for the new tab.
    refreshSharedAnswers();
  };

  const getTabCompletion = (tab) => {
    const ans = tabAnswers[tab] || {};
    let total = 0;
    let answered = 0;
    buildSections(scenarioForTab(tab)).forEach((s) => {
      if (s.conditional && !showProduction(tab)) return;
      s.questions.forEach((q) => {
        if (!REQUIRED_BY_TYPE(q)) return;
        total++;
        const v = ans[q.field_key || q.id];
        let isAns = false;
        if (NUMERIC_TYPES.has(q.answer_type)) isAns = v !== undefined && v !== null && v !== "";
        else if (TEXT_TYPES.has(q.answer_type) || LONGTEXT_TYPES.has(q.answer_type)) isAns = typeof v === "string" && v.trim().length > 0;
        else if (q.answer_type === "multi_select") isAns = Array.isArray(v) && v.length > 0;
        else isAns = !!v;
        if (isAns) answered++;
      });
    });
    return total > 0 ? Math.round((answered / total) * 100) : 0;
  };

  // Live per-tab validation errors for numeric inputs (positive only, min/max, warehouse_count >= 1)
  const tabErrors = (() => {
    const out = {};
    const ans = tabAnswers[activeTab] || {};
    apiSections.forEach((s) => {
      if (s.conditional && !showProduction(activeTab)) return;
      s.questions.forEach((q) => {
        if (!NUMERIC_TYPES.has(q.answer_type)) return;
        const key = q.field_key || q.id;
        const raw = ans[key];
        if (raw === undefined || raw === null || raw === "") return;
        const n = Number(raw);
        if (Number.isNaN(n)) { out[key] = "Numeric value required"; return; }
        if (n < 0) { out[key] = "Must be a positive value"; return; }
        if (q.min_value !== null && q.min_value !== undefined && n < Number(q.min_value)) {
          out[key] = `Minimum ${q.min_value}`; return;
        }
        if (q.max_value !== null && q.max_value !== undefined && n > Number(q.max_value)) {
          out[key] = `Maximum ${q.max_value}`; return;
        }
      });
    });
    return out;
  })();

  // Required-question validation shared across all scenarios. Scans every tab
  // (Cloud Migration uses one synthetic landscape tab) and every visible
  // section, honouring the production conditional filter. Returns the total
  // count of unanswered required questions plus the first tab/section that
  // needs attention and a per-tab map of the missing field keys.
  const computeMissing = useCallback((answersBySite) => {
    let total = 0;
    let firstTab = null;
    let firstSectionId = null;
    const perTabKeys = {};
    (repSites || []).forEach((tab) => {
      const ans = (answersBySite || {})[tab] || {};
      buildSections(scenarioForTab(tab)).forEach((s) => {
        if (s.conditional && !showProduction(tab)) return;
        s.questions.forEach((q) => {
          if (!REQUIRED_BY_TYPE(q)) return;
          const key = q.field_key || q.id;
          if (isAnsweredValue(q, ans[key])) return;
          total += 1;
          (perTabKeys[tab] ||= {})[key] = true;
          if (!firstTab) { firstTab = tab; firstSectionId = s.id; }
        });
      });
    });
    return { total, firstTab, firstSectionId, perTabKeys };
  }, [repSites, buildSections, scenarioForTab, showProduction]);

  const missingInfo = useMemo(() => computeMissing(tabAnswers), [computeMissing, tabAnswers]);

  const isOwner = !!(currentEmail && ownerEmail &&
    currentEmail.trim().toLowerCase() === ownerEmail.trim().toLowerCase());

  // Explicit Save Evaluation — completes THIS browser's pending field saves.
  // Never resends the full local answers object; never submits/scores.
  const handleSaveEvaluation = async () => {
    setSubmitError("");
    setSaveMsg("");
    setSavingEval(true);
    try {
      await flushPendingSaves();
      setSaveStatus("saved");
      setSaveMsg("Evaluation saved successfully. You can resume it anytime.");
    } catch (e) {
      setSubmitError("Some answers couldn't be saved. Please try again.");
    } finally {
      setSavingEval(false);
    }
  };

  // Owner clicks Submit → flush + pull latest SERVER state, validate the
  // COMBINED (owner + contributor) answers, then open the confirmation modal.
  const handleSubmitClick = async () => {
    setSubmitError("");
    setSaveMsg("");
    setSubmitting(true);
    try {
      await flushPendingSaves();
      const id = localStorage.getItem("warehousex_assessment_id");
      let serverM3 = tabAnswers;
      try {
        const fresh = await getAssessmentById(id);
        if (fresh?.module_data?.[answerRoot]) {
          serverM3 = fresh.module_data[answerRoot];
          mergeServerAnswers(serverM3);
        }
      } catch { /* fall back to local snapshot */ }
      // Validate against the MERGED view: local answers (which include the
      // Admin defaults hydrated on load) overlaid with any fresh non-empty
      // server answers from other contributors. This avoids a false "required
      // question missing" when a default persist hasn't round-tripped yet.
      const mergedForValidation = {};
      (repSites || []).forEach((t) => {
        const m = { ...((tabAnswers || {})[t] || {}) };
        Object.entries((serverM3 || {})[t] || {}).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== "") m[k] = v;
        });
        mergedForValidation[t] = m;
      });
      const miss = computeMissing(mergedForValidation);
      if (miss.total > 0) {
        setTriedSubmit(true);
        if (miss.firstTab && miss.firstTab !== activeTab) {
          prevTabRef.current = activeTab;
          setActiveTab(miss.firstTab);
        }
        setFocusSectionId(miss.firstSectionId);
        setSubmitError(
          `Please answer all required questions before submitting. ${miss.total} required question${miss.total > 1 ? "s" : ""} still ${miss.total > 1 ? "need" : "needs"} an answer.`
        );
        setTimeout(() => {
          const el = document.querySelector(`[data-m3-section="${miss.firstSectionId}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
        return;
      }
      setShowSubmitConfirm(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmed submit (owner only). Server finalizes on the latest shared state
  // — we never send the owner's local answers object.
  const doSubmit = async () => {
    setShowSubmitConfirm(false);
    setSubmitting(true);
    try {
      const assessmentId = localStorage.getItem("warehousex_assessment_id");
      const email = localStorage.getItem("warehousex_email");
      if (assessmentId) {
        await flushPendingSaves();
        await submitAssessment(assessmentId, email);
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        try {
          fetch(`${BACKEND_URL}/api/assessments/${assessmentId}/process`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }).catch(() => { /* results are consultant-side only */ });
        } catch { /* no-op */ }
      }
      trackEvent("assessment_submitted", email);
      try {
        localStorage.setItem("warehousex_submitted_scenario", scenarioType || "");
        localStorage.removeItem("warehousex_assessment_id");
        localStorage.removeItem("warehousex_email");
      } catch { /* ignore */ }
      navigate("/evaluate/submitted", { replace: true, state: { scenario: scenarioType } });
    } catch (e) {
      console.error("Failed to submit:", e);
      setSubmitError(
        e?.response?.status === 403
          ? "Only the evaluation owner can submit this assessment."
          : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[#6B7280]">Loading evaluation…</p>
        </main>
      </div>
    );
  }

  if (repSites.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <main className="flex-1 pt-8 sm:pt-10 pb-12">
          <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Demo profile applied banner — keep visible on guard views too. */}
            <DemoProfileBanner stepNumber={3} />
            <div className="text-center pt-12">
              <p className="text-sm text-[#6B7280] mb-4">No warehouse types found. Please complete Module 2 first.</p>
              <Button onClick={() => navigate("/evaluate/module-2")} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm">Go to Module 2</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentAnswers = tabAnswers[activeTab] || {};
  const overallCompletion = repSites.length > 0
    ? Math.round(repSites.reduce((sum, s) => sum + getTabCompletion(s), 0) / repSites.length)
    : 0;

  // Sections visible for the active tab (honours the greenfield/brownfield + production filter).
  const visibleSections = apiSections.filter((s) => !s.conditional || showProduction(activeTab));
  const assessmentType = activeScenario === "cloud_migration" ? "Cloud Migration" : "Warehouse";
  const activeSectionQuestionIds = (visibleSections.find((s) => s.id === visibleSectionId)?.questions || [])
    .map((q) => q.field_key || q.id);

  // Merge numeric errors with required-question errors (only after a submit
  // attempt) so missing required fields show an inline "Required" message.
  const activeRequiredErrors = (triedSubmit && missingInfo.perTabKeys[activeTab])
    ? Object.fromEntries(Object.keys(missingInfo.perTabKeys[activeTab]).map((k) => [k, "Required"]))
    : {};
  const sectionErrors = { ...tabErrors, ...activeRequiredErrors };
  const missingSectionIds = new Set(
    triedSubmit
      ? visibleSections
          .filter((s) => s.questions.some((q) => REQUIRED_BY_TYPE(q) && activeRequiredErrors[q.field_key || q.id]))
          .map((s) => s.id)
      : []
  );

  return (
    <div
      className="min-h-screen flex flex-col bg-[#F8FAFC]"
      onFocusCapture={(e) => {
        const el = e.target.closest?.("[data-field-key]");
        const fk = el?.getAttribute("data-field-key");
        if (fk) setActiveQuestionId(fk);
      }}
    >
      <Header />

      {/* Top-level 3-step progress */}
      <StepProgress currentStep={3} />

      <main className="flex-1 pt-8 sm:pt-10 pb-12">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Demo profile applied banner — shared across Module 1/2/3. */}
          <DemoProfileBanner stepNumber={3} />

          {/* Page header */}
          <div className="mb-5">
            <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5 mb-3">
              <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
              <span className="text-xs font-medium text-[#025979]">Step 3 — Process Evaluation</span>
            </div>
            <h1 data-testid="module3-title" className="text-xl sm:text-2xl font-bold tracking-tight text-[#1F2937]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Warehouse Process Evaluation
            </h1>
            <p className="mt-2 text-sm text-[#6B7280] leading-relaxed max-w-lg">
              Answer the following questions for each representative warehouse so SmartEvaluate can determine the right SAP warehouse strategy.
            </p>
          </div>

          {/* Contributors — add colleagues to this evaluation (data model only) */}
          <ContributorsSection
            assessmentId={localStorage.getItem("warehousex_assessment_id")}
            ownerEmail={ownerEmail}
            currentEmail={currentEmail}
            sections={apiSections.map((s) => ({ id: s.id, title: s.title }))}
            groups={groups.map((g) => ({ id: g.id, name: g.name }))}
          />

          {!isOwner && (myAssignedGroups.length > 0 || myAssignedSections.length > 0) && (
            <div data-testid="my-assigned-banner" className="mb-4 px-4 py-2.5 bg-[#E0F2FE]/50 border border-[#025979]/15 rounded-sm text-xs text-[#025979] space-y-0.5">
              {myAssignedGroups.length > 0 && (
                <div>
                  <span className="font-semibold">Your assigned groups:</span>{" "}
                  {myAssignedGroups.includes("*")
                    ? "All Warehouse Groups"
                    : myAssignedGroups.map((id) => groupById[id]?.name).filter(Boolean).join(", ")}
                </div>
              )}
              {myAssignedSections.length > 0 && (
                <div>
                  <span className="font-semibold">Your assigned processes:</span>{" "}
                  {myAssignedSections.includes("*")
                    ? "All Processes"
                    : myAssignedSections.map((id) => SECTION_TITLES[id] || (apiSections.find((s) => s.id === id)?.title) || id).join(", ")}
                </div>
              )}
            </div>
          )}

          {/* Warehouse tab bar — each tab surfaces the per-site completion %
              prominently in the brand color plus a thin fill bar at the
              bottom. This is now the single source of truth for progress
              (no subsection count or thin bar in the SectionProgress row). */}
          <div className="sticky top-[179px] z-30 bg-[#F8FAFC] pb-3 pt-3 -mx-4 px-4 sm:-mx-6 sm:px-6" data-testid="module3-tab-bar">
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm flex overflow-x-auto">
              {repSites.map((site) => {
                const pct = getTabCompletion(site);
                const isActive = activeTab === site;
                const label = tabLabel(site);
                return (
                  <button
                    key={site}
                    type="button"
                    data-testid={`tab-${site}`}
                    onClick={() => switchTab(site)}
                    className={[
                      "relative flex-1 min-w-0 px-5 py-3.5 transition-colors text-left",
                      isActive
                        ? "bg-[#E0F2FE]/60"
                        : "hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={[
                          "truncate transition-colors",
                          isActive
                            ? "text-sm font-semibold text-[#1F2937]"
                            : "text-sm font-medium text-[#4B5563]",
                        ].join(" ")}
                        style={{ fontFamily: "Chivo, sans-serif" }}
                      >
                        {label}
                      </span>
                      <span
                        data-testid={`tab-${site}-pct`}
                        className={[
                          "shrink-0 tabular-nums transition-colors",
                          isActive
                            ? "text-base font-bold text-[#025979]"
                            : "text-sm font-semibold text-[#025979]/75",
                        ].join(" ")}
                      >
                        {pct}%
                      </span>
                    </div>
                    {/* Thin completion fill — brighter + taller on the active tab */}
                    <div
                      className={[
                        "absolute left-0 right-0 bottom-0 rounded-b-sm overflow-hidden bg-[#E5E7EB] transition-[height]",
                        isActive ? "h-1" : "h-[3px]",
                      ].join(" ")}
                      aria-hidden
                    >
                      <div
                        className={[
                          "h-full rounded-r-full transition-[width] duration-500 ease-out",
                          isActive ? "bg-[#025979]" : "bg-[#03738C]/70",
                        ].join(" ")}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main content + Insights panel — 2-column grid on xl+.
              Below xl the insights panel is hidden so mobile / tablet remain
              completely unchanged. */}
          <div className="mt-1 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] xl:gap-6">
            <div>
              {/* Sections — pure vertical accordion flow */}
              <div className="space-y-3">
                {visibleSections.map((section, idx) => (
                  <CollapsibleSection
                    key={`${activeTab}-${section.id}`}
                    section={section}
                    answers={currentAnswers}
                    errors={sectionErrors}
                    onChange={updateAnswer}
                    tabId={activeTab}
                    defaultOpen={idx === 0 || section.id === focusSectionId}
                    forceOpen={section.id === focusSectionId}
                    highlight={missingSectionIds.has(section.id)}
                    assigned={
                      (myAssignedGroups.includes("*") || myAssignedGroups.includes(activeTab)) &&
                      (myAssignedSections.includes("*") || myAssignedSections.includes(section.id))
                    }
                  />
                ))}
              </div>

              {/* AI Guidance */}
              <div data-testid="module3-ai-guidance" className="mt-5 flex items-start gap-2.5 bg-white border border-[#E5E7EB] rounded-sm px-4 py-3 shadow-sm">
                <Info className="w-4 h-4 text-[#025979] mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-xs text-[#6B7280] leading-relaxed">
                  SmartEvaluate uses your warehouse structure to determine the optimal SAP architecture, including potential hybrid scenarios across different warehouse types.
                </span>
              </div>

              {/* Tab completion summary */}
              <div className="mt-4 bg-white border border-[#E5E7EB] rounded-sm p-4 shadow-sm">
                <p className="text-xs font-medium text-[#111827] mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>Completion Summary</p>
                <div className="space-y-2">
                  {repSites.map((site) => {
                    const pct = getTabCompletion(site);
                    return (
                      <div key={site} className="flex items-center gap-3">
                        <span className="text-xs text-[#6B7280] w-48 truncate">{tabLabel(site)}</span>
                        <div className="flex-1">
                          <Progress value={pct} className="h-1 [&>div]:bg-[#025979]" />
                        </div>
                        <span className={`text-xs font-medium w-10 text-right ${pct === 100 ? "text-[#025979]" : "text-[#9CA3AF]"}`}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-[#9CA3AF] mt-2">Overall: {overallCompletion}% complete</p>
              </div>

              {/* Navigation */}
              {submitError && (
                <p data-testid="module3-submit-error" className="mt-6 -mb-2 text-sm text-red-600 font-medium">
                  {submitError}
                </p>
              )}
              {saveMsg && (
                <p data-testid="module3-save-msg" className="mt-6 -mb-2 text-sm text-[#065F46] font-medium">
                  {saveMsg}
                </p>
              )}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <Button
                  data-testid="module3-back-btn"
                  variant="outline"
                  onClick={async () => { await flushPendingSaves(); navigate("/evaluate/module-2"); }}
                  className="rounded-sm text-sm border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back
                </Button>
                <div className="flex items-center gap-3">
                  {saveStatus !== "idle" && (
                    <span data-testid="module3-save-status" className="text-xs text-[#6B7280] tabular-nums">
                      {saveStatus === "saving" ? "Saving…" : "Saved"}
                    </span>
                  )}
                  <Button
                    data-testid="module3-save-eval-btn"
                    variant="outline"
                    onClick={handleSaveEvaluation}
                    disabled={savingEval}
                    className="rounded-sm text-sm border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors disabled:opacity-50"
                  >
                    {savingEval ? "Saving…" : "Save Evaluation"}
                  </Button>
                  {isOwner && (
                    <Button
                      data-testid="module3-submit-btn"
                      onClick={handleSubmitClick}
                      disabled={submitting}
                      className="bg-[#025979] hover:bg-[#03738C] text-white px-8 py-2.5 text-sm font-medium rounded-sm transition-colors shadow-sm disabled:opacity-50"
                    >
                      {submitting ? "Submitting…" : "Submit Evaluation"}
                      {!submitting && <ArrowRight className="ml-2 w-4 h-4" />}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Right — SmartEvaluate Insights (sticky, desktop only) */}
            <div className="hidden xl:block">
              <div className="sticky top-[260px]">
                <InsightsPanel
                  activeTab={activeTab}
                  tabLabel={tabLabel}
                  answers={currentAnswers}
                  visibleSections={visibleSections}
                  activeSectionId={visibleSectionId}
                />
              </div>
            </div>
          </div>

          {/* Floating SAP Help — Joule-style icon + side drawer.
              Available from anywhere on Step 3; context-aware suggestions
              follow the currently-visible section. */}
          <HelpAssistantPanel
            activeSectionId={visibleSectionId}
            assessmentType={assessmentType}
            activeQuestionId={activeQuestionId}
            sectionQuestionIds={activeSectionQuestionIds}
          />

          {/* Owner-only submit confirmation */}
          <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
            <DialogContent data-testid="submit-confirm-modal" className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>Submit Evaluation?</DialogTitle>
                <DialogDescription className="text-sm text-[#6B7280] leading-relaxed pt-1">
                  Once submitted, this evaluation will be finalized and sent for processing. Please make sure all contributors have completed their inputs before submitting.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  data-testid="submit-confirm-cancel"
                  variant="outline"
                  onClick={() => setShowSubmitConfirm(false)}
                  className="rounded-sm text-sm"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-confirm-yes"
                  onClick={doSubmit}
                  disabled={submitting}
                  className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Yes, Submit Evaluation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
      <Footer />
    </div>
  );
}
